import { LoadingSpinner } from "@/components/loading-spinner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsSticking } from "@/hooks/use-sticky";
import { T } from "@/i18n";
import { cn } from "@/lib/utils";
import {
  Accommodation,
  ReservationClient,
  ReservationDay,
  ReservationService,
} from "@/models";
import { LazyOperationEntity } from "@/models/commons";
import { CreateReservation, Reservation } from "@/models/reservations";
import { CircleAlert, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useUpsertReservation } from "../../-hooks";
import { ScheduleManager } from "../../-managers";
import { ClientsTab } from "./clients";
import { ReservationFirstStepModel, ReservationFormContext } from "./contex";
import { ReservationTab } from "./reservation";
import { ScheduleTab } from "./schedule";
const tabs = [
  {
    value: "details",
    label: "reservations.form.tabs.details",
    content: <ReservationTab />,
  },
  {
    value: "clients",
    label: "reservations.form.tabs.clients",
    content: <ClientsTab />,
  },
  {
    value: "schedule",
    label: "reservations.form.tabs.schedule",
    content: <ScheduleTab />,
  },
] as const;

const MockReservationId = -1;

export function ReservationFormProvider({
  model,
  clients,
  accommodations,
  reservationDays,
  services,
  onSuccess,
}: {
  model?: Reservation;
  clients: ReservationClient[];
  accommodations: Accommodation[];
  services: ReservationService[];
  reservationDays: ReservationDay[];
  onSuccess: (id: string) => void;
}) {
  const { ref: tabListRef } = useIsSticking<HTMLDivElement>();

  const reservationId = Number.parseInt(
    model?.id ?? MockReservationId.toString(),
  );

  const { mutate, isPending } = useUpsertReservation(
    reservationId,
    model ? "update" : "create",
    {
      onSuccess,
    },
  );
  const [dirty, setDirty] = useState(false);

  const [reservation, setReservation] = useState<
    LazyOperationEntity<ReservationFirstStepModel | null>
  >(
    model
      ? { operation: null, value: model }
      : { operation: "noop", value: null },
  );

  const [reservationClients, setReservationClients] = useState<
    Array<LazyOperationEntity<ReservationClient>>
  >(
    clients.map((c) => ({
      operation: null,
      value: c,
    })),
  );

  const [leaderId, setLeaderId] = useState<number | undefined | null>(
    model?.groupLeaderId,
  );

  const [scheduleManager] = useState(
    new ScheduleManager(
      reservationId.toString(),
      accommodations,
      services,
      reservationDays,
    ),
  );

  const [days, realSetDays] = useState(() => scheduleManager.days);

  const setDays = useCallback(
    (days: typeof scheduleManager.days) => {
      realSetDays(() => days);
    },
    [scheduleManager],
  );

  useEffect(() => {
    scheduleManager.listen((d) => {
      setDays(d);
      setDirty(true);
    });
    return () => scheduleManager.ignore();
  }, [scheduleManager, setDays]);

  const [errors, setErrors] = useState<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);

  const [validators, setValidators] = useState<
    [(() => boolean) | null, (() => boolean) | null, (() => boolean) | null]
  >([null, null, null]);

  const setStepError = (val: boolean, idx: 0 | 1 | 2) => {
    setErrors((prev) => {
      if (val === errors[idx]) return prev;
      const newErrors: typeof prev = [...prev];
      newErrors[idx] = val;
      return newErrors;
    });
  };

  const save = () => {
    if (!dirty) return;
    setErrors([false, false, false]);
    const stepOneValid = validators[0]?.();
    const stepTwoValid = validators[1]?.() ?? true;
    const stepThreeValid = validators[2]?.() ?? true;
    setStepError(!stepOneValid, 0);
    setStepError(!stepTwoValid, 1);
    setStepError(!stepThreeValid, 2);

    if (!stepOneValid || !stepTwoValid || !stepThreeValid) return;

    mutate({
      reservation: {
        operation: reservation.operation,
        value: {
          ...reservation.value,
          groupLeaderId: leaderId!,
        } as CreateReservation,
      },
      clients: reservationClients,
      schedule: days,
    });
  };

  function handleSetReservation(
    value: ReservationFirstStepModel,
    updateDirty = true,
  ) {
    const newVal: typeof reservation = { ...reservation, value };
    if (newVal.operation === "noop") newVal.operation = "create";
    if (newVal.operation === null) newVal.operation = "update";
    setReservation(newVal);
    if (updateDirty) setDirty(true);
  }

  function handleSetReservationValidator(cb: () => boolean) {
    const newModel: typeof validators = [...validators];
    newModel[0] = cb;
    setValidators(newModel);
  }

  function handleAddReservationClient(client: ReservationClient) {
    setReservationClients((prev) => [
      ...prev,
      {
        operation: "create",
        value: {
          ...client,
          reservationId,
        },
      },
    ]);
    setDirty(true);
  }

  function handleSetGroupLeader(id: string) {
    setLeaderId(+id);
    handleSetReservation(reservation.value!);
  }

  function handleRemoveReservationClient(clientId: string) {
    const getIndex = reservationClients.findIndex(
      (c) => c.value.clientId.toString() === clientId,
    );

    const client = reservationClients[getIndex];
    if (client.operation === "create") {
      setReservationClients((prev) =>
        prev.filter((c) => c.value.clientId !== client.value.clientId),
      );
    } else {
      setReservationClients((prev) =>
        prev.map((c) => ({
          ...c,
          operation: c.value.clientId === +clientId ? "delete" : c.operation,
        })),
      );
    }

    days
      .map((d) => d.accommodations)
      .flat()
      .map((a) => a.value)
      .filter(
        (a) =>
          a.reservationClients.findIndex(
            (c) => c.clientId.toString() === clientId,
          ) >= 0,
      )
      .forEach((acc) => {
        const newAcc: typeof acc = {
          ...acc,
          reservationClients: acc.reservationClients.filter(
            (c) => c.clientId !== +clientId,
          ),
        };
        scheduleManager.handleUpdateAccommodation(acc.day, newAcc);
      });
    setDirty(true);
  }

  function handleUpdateReservationClient(
    clientId: number,
    client: ReservationClient,
  ) {
    setReservationClients((prev) =>
      prev.map((c) => {
        if (c.value.clientId !== clientId) return c;

        const newC: typeof c = { ...c };

        newC.value = client;
        if (newC.operation === null) newC.operation = "update";
        return newC;
      }),
    );
    setDirty(true);
  }

  function handleSetReservationClientsValidator(cb: () => boolean) {
    const newModel: typeof validators = [...validators];
    newModel[1] = cb;
    setValidators(newModel);
  }

  return (
    <ReservationFormContext.Provider
      value={{
        reservation: reservation.value,
        setReservation: handleSetReservation,
        setReservationValidator: handleSetReservationValidator,
        isEdit: !!model,
        isDirty: dirty,
        reservationClients: reservationClients
          .filter((c) => c.operation !== "delete")
          .map((c) => c.value),
        addReservationClient: handleAddReservationClient,
        setGroupLeader: handleSetGroupLeader,
        removeReservationClient: handleRemoveReservationClient,
        updateReservationClient: handleUpdateReservationClient,
        groupLeaderId: leaderId ?? -1,
        setReservationClientValidator: handleSetReservationClientsValidator,
        days,
        scheduleManager,
      }}
    >
      <Tabs defaultValue={tabs[0].value} className="grid w-full items-center">
        <TabsList
          className="sticky top-4 mx-auto transition-shadow data-[stick=true]:shadow-lg"
          ref={tabListRef}
        >
          {tabs.map((t, i) => (
            <TabsTrigger
              key={t.value}
              className={cn(
                errors[i] &&
                  "text-destructive data-[state=active]:text-destructive",
              )}
              value={t.value}
            >
              {errors[i] ? (
                <CircleAlert width={16} height={16} className="mr-1" />
              ) : (
                <></>
              )}
              <T tKey={t.label} />
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((t) => (
          <TabsContent value={t.value} key={t.value}>
            {t.content}
          </TabsContent>
        ))}
      </Tabs>
      {dirty ? (
        <div className="fixed bottom-8 right-8 z-10 flex items-center gap-6">
          <Button
            size="xxl"
            className="bg-green-600 text-white hover:bg-green-600/90"
            disabled={isPending}
            onClick={() => {
              if (isPending) return;
              save();
            }}
          >
            {isPending ? <LoadingSpinner /> : <Save />}
          </Button>
        </div>
      ) : (
        <></>
      )}
    </ReservationFormContext.Provider>
  );
}
